import type { IBizEntity, IFieldMeta } from '@optima-stride-ai/core';
import type { IHierarchyMove } from '@optima-stride-ai/optima-components';

/**
 * Resolves exactly one typed record returned by a hierarchy-specific store read.
 * @param move - Requested move, including the expected entity category.
 * @param entities - Records returned by the store, without a cached-row fallback.
 * @returns The matching typed record.
 * @throws When the entity disappeared, is duplicated, or its category changed.
 * @example const entity = resolveHierarchyRecord(move, await loadById(move.entityId));
 */
export function resolveHierarchyRecord(move: IHierarchyMove, entities: IBizEntity[] | undefined): IBizEntity {
  const matches = entities?.filter(entity => entity.id?.toString() === move.entityId) ?? [];
  const entity = matches[0];
  if (matches.length !== 1 || !entity?.type?.category || entity.type.category !== move.entity.type?.category) {
    throw new Error(`Unable to confirm typed entity ${move.entityId} for hierarchy update`);
  }
  return entity;
}

/**
 * Creates an isolated PUT payload, preserving freshly loaded entity fields.
 * This detects already-observed parent conflicts, not races after the read;
 * atomic concurrency protection must be enforced by the backend.
 * @param move - Requested hierarchy move with its original parent key.
 * @param entity - Record loaded from its typed store immediately before saving.
 * DateRange and other model instances are retained: the existing serializers read
 * their getters and replace the top-level fields. Only metadata entries that those
 * serializers mutate are copied. This is not a general-purpose deep clone.
 * @returns A payload with the requested parent and isolated mutable metadata.
 * @throws When the parent changed since the drag began.
 * @example const payload = createHierarchyMovePayload(move, freshEntity);
 */
export function createHierarchyMovePayload(move: IHierarchyMove, entity: IBizEntity): IBizEntity {
  if ((entity.parentId?.toString() ?? '') !== move.previousParentKey) {
    throw new Error(`Entity ${move.entityId} changed parent; refresh before moving it`);
  }
  const payload: IBizEntity & { logFieldsMeta?: IFieldMeta[] } = { ...entity, parentId: move.nextParentId };
  if (Array.isArray(entity.additionalInfo)) {
    payload.additionalInfo = entity.additionalInfo.map(info => ({ ...info }));
  }
  const logFieldsMeta = (entity as IBizEntity & { logFieldsMeta?: IFieldMeta[] }).logFieldsMeta;
  if (Array.isArray(logFieldsMeta)) {
    payload.logFieldsMeta = logFieldsMeta.map(field => ({ ...field }));
  }
  return payload;
}